import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Section, RowCard, ListCard, ListRow, Button, Field, Card, Quiet, Note, C, when, errText, plural } from "./ui.jsx";
import { callLine, laneLine, equipmentRows } from "./dispatchView.js";

/**
 * Dispatch (the schematic's Dispatch and Drivers boards): find anything, what needs you, the four
 * lists, and what you touched lately. A dispatcher who sees only their own drivers and loads sees
 * only those everywhere here: the server narrows every list (backend access.mjs).
 */

const AREAS = [
  { to: "/carrier/dispatch/drivers", label: "Drivers" },
  { to: "/carrier/dispatch/loads", label: "Loads" },
  { to: "/carrier/dispatch/trucks", label: "Trucks" },
  { to: "/carrier/dispatch/trailers", label: "Trailers" },
];

/** Where an item opens: its load, else its driver. */
export const itemHref = (i) => (i.loadId ? `/carrier/dispatch/loads/${i.loadId}` : i.driverId ? `/carrier/dispatch/drivers/${i.driverId}` : "/carrier/dispatch");

export function SearchResults({ q, results }) {
  if (!results) return null;
  const rows = [
    ...results.drivers.map((d) => ({ key: `d${d.id}`, to: `/carrier/dispatch/drivers/${d.id}`, name: d.name, line: ["Driver", d.truck ? `Truck ${d.truck}` : null].filter(Boolean).join(" · ") })),
    ...results.loads.map((l) => ({ key: `l${l.id}`, to: `/carrier/dispatch/loads/${l.id}`, name: l.ref, line: ["Load", l.driverName, laneLine(l)].filter(Boolean).join(" · ") })),
  ];
  return (
    <Section id="results-title" title={`Results for "${q}" (${rows.length})`}>
      {rows.length === 0 ? <Quiet>Nothing matches. Try a name, a load number, or a truck or trailer number.</Quiet> : (
        <ListCard label="Search results">
          {rows.map((r, i) => <ListRow key={r.key} to={r.to} name={r.name} last={i === rows.length - 1}><span>{r.line}</span></ListRow>)}
        </ListCard>
      )}
    </Section>
  );
}

export function DispatchView({ company, stage, me, desk, recent, error, q, setQ, onSearch, results, onHandled, busy, msg }) {
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier">Home</Back>
      <Title sub={me?.own ? "Showing only your drivers and loads." : null}>Dispatch</Title>
      <form role="search" onSubmit={(e) => { e.preventDefault(); onSearch(); }} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <label htmlFor="find" style={{ fontSize: 14, fontWeight: 600 }}>Find a driver, load, truck or trailer</label>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <input id="find" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or number"
            style={{ flex: "1 1 320px", minWidth: 0, minHeight: 52, padding: "0 16px", boxSizing: "border-box", font: "inherit", fontSize: 17, border: `1px solid ${C.field}`, borderRadius: 8, background: C.card, color: C.ink }} />
          <Button type="submit" kind="secondary" disabled={!q.trim()} style={{ minHeight: 52, padding: "0 24px", fontSize: 16 }}>Search</Button>
        </div>
      </form>
      <SearchResults q={q} results={results} />
      {error && <Quiet>{error}</Quiet>}

      {desk && (
        <Section id="needs-title" title={`Needs you (${desk.length})`}>
          {desk.length === 0 && <Quiet>Nothing is waiting on a dispatcher.</Quiet>}
          {desk.map((i) => (
            <RowCard key={i.id} strong title={i.title} sub={i.detail} to={itemHref(i)} label={`Open ${i.title}`}
              extra={(i.persona || i.seeded || i.handleable) ? (
                <span style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                  {(i.persona || i.seeded) && <span style={{ fontSize: 14, color: C.muted }}>{i.persona ? "A demonstration: an AI played the driver." : "Seeded for a demonstration: this call was not placed."}</span>}
                  {i.handleable && <Button kind="quiet" disabled={busy} onClick={() => onHandled(i.id)} style={{ padding: 0, minHeight: 32 }} label={`Mark ${i.title} handled`}>Mark handled</Button>}
                </span>
              ) : null} />
          ))}
          <Note msg={msg} />
        </Section>
      )}

      <nav aria-label="Dispatch areas" style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        {AREAS.map((a) => (
          <Link key={a.to} to={a.to} className="cp-row" style={{ flex: "1 1 160px", minHeight: 72, display: "flex", alignItems: "center", justifyContent: "center", background: C.card,
            border: `1px solid ${C.field}`, borderRadius: 12, fontSize: 20, fontWeight: 600, color: C.ink, textDecoration: "none" }}>{a.label}</Link>
        ))}
      </nav>

      {recent && (
        <Section id="recent-title" title="Recent" sub="Drivers and loads you've worked with lately.">
          {recent.length === 0 ? <Quiet>Nothing yet.</Quiet> : (
            <ListCard label="Recent">
              {recent.map((r, i) => (
                <ListRow key={`${r.kind}-${r.id}`} to={`/carrier/dispatch/${r.kind === "load" ? "loads" : "drivers"}/${r.id}`} name={r.name} last={i === recent.length - 1}>
                  <span>{`${r.kind === "load" ? "Load" : "Driver"} · ${r.what} · ${when(r.at)}`}</span>
                </ListRow>
              ))}
            </ListCard>
          )}
        </Section>
      )}
    </Shell>
  );
}

export function Dispatch() {
  const { carrier, me } = useCarrier();
  const [desk, setDesk] = useState(null);
  const [recent, setRecent] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    let alive = true;
    freight.desk().then((d) => alive && setDesk(d)).catch((e) => alive && setError(`Needs you could not be read: ${errText(e)}`));
    freight.recent().then((r) => alive && setRecent(r)).catch(() => alive && setRecent([]));
    return () => { alive = false; };
  }, []);
  const onSearch = async () => {
    try { setResults(await freight.searchDispatch(q.trim())); } catch (e) { setError(`Search did not work: ${errText(e)}`); }
  };
  const onHandled = async (itemId) => {
    setBusy(true); setMsg(null);
    try { setDesk(await freight.markHandled(itemId)); setMsg({ ok: true, text: "Marked handled." }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return <DispatchView company={carrier.name} stage={carrier.stage} me={me} desk={desk} recent={recent} error={error}
    q={q} setQ={(v) => { setQ(v); if (!v.trim()) setResults(null); }} onSearch={onSearch} results={results} onHandled={onHandled} busy={busy} msg={msg} />;
}

/**
 * Old links: the recruiter email used to open /carrier/dispatch?view=recruiting&applicant=ID (the
 * board before these pages). Emails already sent still carry it, so it lands on the applicant page.
 */
export function DispatchOrOldLink() {
  const params = new URLSearchParams(useLocation().search);
  if (params.get("view") === "recruiting") {
    const a = params.get("applicant");
    return <Navigate to={/^\d{1,10}$/.test(a || "") ? `/carrier/hiring/${a}` : "/carrier/hiring"} replace />;
  }
  return <Dispatch />;
}

/* ── the four lists ──────────────────────────────────────────────────────────── */

const SHOW = 25;

export function DriversView({ company, stage, me, drivers, error }) {
  const [all, setAll] = useState(false);
  const list = drivers || [];
  const shown = all ? list : list.slice(0, SHOW);
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/dispatch">Dispatch</Back>
      <Title sub={me?.own ? "Your drivers, latest activity first." : "Every driver, latest activity first."}>Drivers</Title>
      {error && <Quiet>{error}</Quiet>}
      {!drivers && !error && <Quiet>Loading…</Quiet>}
      {drivers && list.length === 0 && <Quiet>{me?.own ? "No drivers are assigned to you yet." : "No drivers on the roster yet."}</Quiet>}
      {shown.length > 0 && (
        <ListCard label="Drivers">
          {shown.map((d, i) => (
            <ListRow key={d.id} to={`/carrier/dispatch/drivers/${d.id}`} name={d.name} last={i === shown.length - 1}>
              <span style={{ color: C.ink }}>{[d.truck ? `Truck ${d.truck}` : null, d.currentLoad ? `On ${d.currentLoad.ref}` : "No open load"].filter(Boolean).join(" · ")}</span>
              <span>{d.consent ? callLine(d.lastCall, when) : "Has not agreed to Alfred's calls"}</span>
            </ListRow>
          ))}
        </ListCard>
      )}
      {!all && list.length > SHOW && <Button kind="quiet" onClick={() => setAll(true)} style={{ alignSelf: "flex-start", padding: 0 }}>{`Show all ${list.length} drivers →`}</Button>}
    </Shell>
  );
}

export function Drivers() {
  const { carrier, me } = useCarrier();
  const [drivers, setDrivers] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    freight.dispatchDrivers().then((d) => alive && setDrivers(d)).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, []);
  return <DriversView company={carrier.name} stage={carrier.stage} me={me} drivers={drivers} error={error} />;
}

/** Until a TMS is connected, a load is added here. */
export function AddLoad({ drivers, onAdded, onCancel }) {
  const [f, setF] = useState({ ref: "", pickupCity: "", pickupAt: "", dropCity: "", deliverBy: "", driverId: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const add = async () => {
    setBusy(true); setMsg(null);
    try {
      const load = await freight.saveLoad({
        ref: f.ref.trim(), status: f.driverId ? "planned" : "needs_driver", driverId: f.driverId ? Number(f.driverId) : null,
        stops: [{ seq: 1, type: "pickup", city: f.pickupCity.trim(), window: f.pickupAt.trim() || "—", state: "pending" },
                { seq: 2, type: "drop", city: f.dropCity.trim(), window: f.deliverBy.trim() || "—", state: "pending" }],
        detail: { source: "dispatch page" },
      });
      onAdded(load);
    } catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Add a load</h2>
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        <Field id="load-ref" label="Load number" value={f.ref} onChange={set("ref")} />
        <Field id="load-pick" label="Pickup city" value={f.pickupCity} onChange={set("pickupCity")} />
        <Field id="load-pick-at" label="Pickup time" value={f.pickupAt} onChange={set("pickupAt")} placeholder="9:00 AM" />
        <Field id="load-drop" label="Delivery city" value={f.dropCity} onChange={set("dropCity")} />
        <Field id="load-drop-at" label="Deliver by" value={f.deliverBy} onChange={set("deliverBy")} placeholder="5:00 PM" />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label htmlFor="load-driver" style={{ fontSize: 14, fontWeight: 600 }}>Driver</label>
          <select id="load-driver" value={f.driverId} onChange={(e) => set("driverId")(e.target.value)}
            style={{ font: "inherit", fontSize: 16, minHeight: 48, padding: "0 12px", border: `1px solid ${C.field}`, borderRadius: 8, background: C.card }}>
            <option value="">Nobody yet</option>
            {(drivers || []).map((d) => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Button disabled={busy || !f.ref.trim()} onClick={add}>Add the load</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
      <Note msg={msg} />
    </Card>
  );
}

export function LoadsView({ company, stage, me, loads, drivers, error, closed, setClosed, adding, setAdding, onAdded }) {
  const list = loads || [];
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/dispatch">Dispatch</Back>
      <Title sub={`${me?.own ? "Your" : "All"} ${closed ? "finished" : "open"} loads, latest activity first.`}>Loads</Title>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {!adding && <Button onClick={() => setAdding(true)}>Add a load</Button>}
        <Button kind="secondary" onClick={() => setClosed(!closed)}>{closed ? "Show open loads" : "Show finished loads"}</Button>
      </div>
      {adding && <AddLoad drivers={drivers} onAdded={onAdded} onCancel={() => setAdding(false)} />}
      {error && <Quiet>{error}</Quiet>}
      {!loads && !error && <Quiet>Loading…</Quiet>}
      {loads && list.length === 0 && <Quiet>{closed ? "No finished loads." : "No open loads."}</Quiet>}
      {list.length > 0 && (
        <ListCard label="Loads">
          {list.map((l, i) => (
            <ListRow key={l.id} to={`/carrier/dispatch/loads/${l.id}`} name={l.ref} last={i === list.length - 1}>
              <span style={{ color: C.ink }}>{[l.driverName || "No driver yet", laneLine(l)].join(" · ")}</span>
              <span>{callLine(l.lastCall, when)}</span>
            </ListRow>
          ))}
        </ListCard>
      )}
    </Shell>
  );
}

export function Loads() {
  const { carrier, me } = useCarrier();
  const nav = useNavigate();
  const [loads, setLoads] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [error, setError] = useState(null);
  const [closed, setClosed] = useState(false);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoads(null);
    freight.dispatchLoads(closed).then((l) => alive && setLoads(l)).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, [closed]);
  useEffect(() => {
    if (!adding || drivers) return;
    freight.dispatchDrivers().then(setDrivers).catch(() => setDrivers([]));
  }, [adding, drivers]);
  return <LoadsView company={carrier.name} stage={carrier.stage} me={me} loads={loads} drivers={drivers} error={error}
    closed={closed} setClosed={setClosed} adding={adding} setAdding={setAdding} onAdded={(l) => nav(`/carrier/dispatch/loads/${l.id}`)} />;
}

const onWords = (d) => (d.currentLoad ? `On ${d.currentLoad.ref}` : "No open load");

/** One truck or trailer shared by several drivers: each driver is their own link (a row link cannot hold links). */
function SharedRow({ row, last }) {
  return (
    <li style={{ borderBottom: last ? "none" : `1px solid ${C.soft}`, padding: "16px 24px", display: "flex", alignItems: "baseline", gap: "6px 16px", flexWrap: "wrap" }}>
      <span style={{ fontSize: 17, fontWeight: 600, minWidth: 140 }}>{row.name}</span>
      <span style={{ fontSize: 16, color: C.muted, flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        {row.type && <span>{row.type}</span>}
        {row.drivers.map((d) => (
          <span key={d.id}><Link to={`/carrier/dispatch/drivers/${d.id}`} style={{ fontWeight: 600, textDecoration: "none" }}>{d.name}</Link>{` · ${onWords(d)}`}</span>
        ))}
      </span>
    </li>
  );
}

/** Trucks and trailers come from the roster: the number on each driver (a TMS would add more). */
export function EquipmentView({ company, stage, kind, drivers, error }) {
  const isTruck = kind === "trucks";
  const { rows, typeOnly } = equipmentRows(drivers || [], kind);
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/dispatch">Dispatch</Back>
      <Title sub={`${isTruck ? "Trucks" : "Trailers"} on your roster, with who drives each.`}>{isTruck ? "Trucks" : "Trailers"}</Title>
      {error && <Quiet>{error}</Quiet>}
      {!drivers && !error && <Quiet>Loading…</Quiet>}
      {drivers && rows.length === 0 && <Quiet>{`No ${isTruck ? "truck" : "trailer"} numbers on the roster yet.`}</Quiet>}
      {rows.length > 0 && (
        <ListCard label={isTruck ? "Trucks" : "Trailers"}>
          {rows.map((r, i) => (r.drivers.length > 1
            ? <SharedRow key={r.key} row={r} last={i === rows.length - 1} />
            : (
              <ListRow key={r.key} to={`/carrier/dispatch/drivers/${r.drivers[0].id}`} name={r.name} last={i === rows.length - 1}>
                <span>{[r.drivers[0].name, r.type, onWords(r.drivers[0])].filter(Boolean).join(" · ")}</span>
              </ListRow>
            )))}
        </ListCard>
      )}
      {drivers && rows.length > 0 && <Quiet>{`${rows.length} ${plural(rows.length, isTruck ? "truck" : "trailer", isTruck ? "trucks" : "trailers")}.`}</Quiet>}
      {typeOnly > 0 && <Quiet>{`${typeOnly} ${plural(typeOnly, "driver has", "drivers have")} a trailer type on file but no trailer number, so ${plural(typeOnly, "it is", "they are")} not counted here.`}</Quiet>}
    </Shell>
  );
}

export function Equipment({ kind }) {
  const { carrier } = useCarrier();
  const [drivers, setDrivers] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    freight.dispatchDrivers().then((d) => alive && setDrivers(d)).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, []);
  return <EquipmentView company={carrier.name} stage={carrier.stage} kind={kind} drivers={drivers} error={error} />;
}
